/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, MagnifyingGlass, ArrowSquareIn, Trash, ArrowsClockwise, ShieldCheck, Paperclip, DownloadSimple, Eye, FileText, Image as ImageIcon, FilePdf } from "@phosphor-icons/react";
import { downloadAuthed } from "@/lib/api";

/**
 * `/inbox/archive` — firm-wide compliance email archive review queue.
 * Every email pulled from every connected mailbox is retained here for 6
 * years. This page surfaces the UNASSIGNED ones so a fee-earner can file
 * them onto a case (assign) or mark them not-a-matter (dismiss).
 */
export default function CommsArchivePage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ unassigned: 0, matched: 0, total: 0 });
  const [status, setStatus] = useState("unassigned");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [assignRow, setAssignRow] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listR, statsR] = await Promise.all([
        API.get(`/comms-archive`, { params: { status, q, limit: 100 } }).then(r => r.data),
        API.get(`/comms-archive/stats`).then(r => r.data).catch(() => null),
      ]);
      setItems(listR.items || []);
      setTotal(listR.total || 0);
      if (statsR) setStats(statsR);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the archive");
    } finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (row) => {
    try {
      await API.post(`/comms-archive/${row.id}/dismiss`);
      toast.success("Removed from the archive");
      setItems(items.filter(i => i.id !== row.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not dismiss"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="comms-archive-page">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <ShieldCheck weight="fill" className="w-6 h-6 text-[#100f5d]" /> Compliance email archive
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every email from every connected mailbox is retained for 6 years. File unassigned mail onto a case, or mark it not-a-matter.
            </p>
          </div>
          <button onClick={load} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="archive-refresh">
            <ArrowsClockwise className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[["unassigned", `Unassigned (${stats.unassigned || 0})`], ["matched", `Filed (${stats.matched || 0})`], ["all", `All (${stats.total || 0})`]].map(([k, label]) => (
            <button key={k} onClick={() => setStatus(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${status === k ? "bg-[#100f5d] text-white border-[#100f5d]" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid={`archive-filter-${k}`}>{label}</button>
          ))}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search subject, sender, recipient…"
              className="pl-8 h-9 text-sm" data-testid="archive-search" />
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 p-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 p-10 text-center bg-white rounded-lg border border-slate-200" data-testid="archive-empty">
            <Archive className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Nothing here. {status === "unassigned" ? "All captured mail is filed to a case." : "No results."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="archive-list">
            {items.map(row => (
              <div key={row.id} className="bg-white rounded-lg border border-slate-200 p-3 flex items-start gap-3" data-testid="archive-row">
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded mt-0.5 shrink-0 ${row.direction === "outgoing" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {row.direction === "outgoing" ? "Sent" : "Recv"}
                </span>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={async () => {
                  setPreview({ ...row, body_html: "Loading…", attachments: null });
                  const [b, a] = await Promise.all([
                    API.get(`/comms-archive/${row.id}/body`).then(r => r.data).catch(() => null),
                    row.has_attachments
                      ? API.get(`/comms-archive/${row.id}/attachments`).then(r => r.data?.attachments || []).catch(() => [])
                      : Promise.resolve([]),
                  ]);
                  setPreview({ ...row, body_html: b?.body_html || row.body_preview || "(no content)", attachments: a });
                }}>
                  <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                    <span className="truncate">{row.subject || "(no subject)"}</span>
                    {row.has_attachments && (
                      <Paperclip weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0"
                        title="Has attachments — filed with its documents" data-testid="archive-has-attachments" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {row.from_email} → {(row.to_emails || []).join(", ") || "—"}
                    {row.status === "matched" && <span className="ml-2 text-emerald-600">· filed</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">{row.body_preview}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {row.status !== "matched" && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAssignRow(row)} data-testid="archive-assign-btn">
                      <ArrowSquareIn className="w-3.5 h-3.5 mr-1" /> Assign to case
                    </Button>
                  )}
                  {row.status !== "matched" && (
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-600 hover:text-rose-700" onClick={() => dismiss(row)} data-testid="archive-dismiss-btn">
                      <Trash className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {total > items.length && <p className="text-xs text-slate-400 text-center py-2">Showing {items.length} of {total}. Refine your search to narrow down.</p>}
          </div>
        )}
      </div>

      {assignRow && <AssignDialog row={assignRow} onClose={() => setAssignRow(null)} onDone={() => { setAssignRow(null); load(); }} />}

      <Dialog open={!!preview} onOpenChange={o => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="archive-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{preview?.subject}</DialogTitle>
            <DialogDescription className="text-xs">{preview?.from_email} → {(preview?.to_emails || []).join(", ")}</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview?.body_html || "" }} />
          {preview?.has_attachments && (
            <div className="mt-4 border-t border-slate-100 pt-3" data-testid="archive-preview-attachments">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                <Paperclip weight="bold" className="w-3.5 h-3.5" /> Attachments
                {preview.attachments === null && <span className="text-slate-400 font-normal">· loading…</span>}
              </p>
              {Array.isArray(preview.attachments) && preview.attachments.length === 0 && (
                <p className="text-xs text-slate-400">Attachments couldn't be loaded (the mailbox may be disconnected).</p>
              )}
              <div className="space-y-2">
                {(preview.attachments || []).map(att => (
                  <ArchiveAttachment key={att.id} archiveId={preview.id} att={att} />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignDialog({ row, onClose, onDone }) {
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get(`/cases`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.cases || []);
      setCases(list);
    }).catch(() => setCases([]));
  }, []);

  const filtered = cases.filter(c => {
    const s = search.toLowerCase();
    return !s || (c.title || "").toLowerCase().includes(s) || (c.property_address || "").toLowerCase().includes(s);
  }).slice(0, 30);

  const assign = async (caseId) => {
    setSaving(true);
    try {
      await API.post(`/comms-archive/${row.id}/assign`, { case_id: caseId });
      toast.success("Filed onto the case's Communications");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="archive-assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Assign to a case</DialogTitle>
          <DialogDescription className="text-xs truncate">{row.subject}</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cases by title or address…"
          className="h-9 text-sm" data-testid="archive-assign-search" />
        <div className="max-h-72 overflow-auto space-y-1 mt-2">
          {filtered.map(c => (
            <button key={c.id} disabled={saving} onClick={() => assign(c.id)}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-100 text-sm disabled:opacity-50"
              data-testid="archive-assign-case-option">
              <div className="font-medium text-slate-900 truncate">{c.title || "Untitled case"}</div>
              <div className="text-xs text-slate-500 truncate">{c.property_address || "—"}</div>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-xs text-slate-400 p-3 text-center">No matching cases.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

function ArchiveAttachment({ archiveId, att }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const ct = (att.content_type || "").toLowerCase();
  const isImage = ct.startsWith("image/");
  const isPdf = ct.includes("pdf") || (att.filename || "").toLowerCase().endsWith(".pdf");
  const canPreview = isImage || isPdf;
  const path = `/comms-archive/${archiveId}/attachments/${encodeURIComponent(att.id)}/download`;

  const togglePreview = async () => {
    if (open) { setOpen(false); return; }
    if (!url) {
      setLoading(true);
      try {
        const resp = await API.get(path, { responseType: "blob" });
        setUrl(URL.createObjectURL(new Blob([resp.data], { type: att.content_type || "application/octet-stream" })));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load attachment");
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    setOpen(true);
  };

  const Icon = isImage ? ImageIcon : isPdf ? FilePdf : FileText;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60" data-testid="archive-attachment">
      <div className="flex items-center gap-2 px-3 py-2">
        <Icon weight="fill" className="w-4 h-4 text-[#100f5d] shrink-0" />
        <span className="text-xs text-slate-700 truncate flex-1" title={att.filename}>{att.filename}</span>
        {att.size ? <span className="text-[11px] text-slate-400 shrink-0">{fmtSize(att.size)}</span> : null}
        {canPreview && (
          <button onClick={togglePreview} disabled={loading}
            className="text-[11px] text-[#100f5d] hover:underline flex items-center gap-1 shrink-0 disabled:opacity-50"
            data-testid="archive-attachment-preview-btn">
            <Eye className="w-3.5 h-3.5" /> {loading ? "…" : open ? "Hide" : "Preview"}
          </button>
        )}
        <button onClick={() => downloadAuthed(path, att.filename)}
          className="text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1 shrink-0"
          data-testid="archive-attachment-download-btn">
          <DownloadSimple className="w-3.5 h-3.5" /> Download
        </button>
      </div>
      {open && url && (
        <div className="px-3 pb-3">
          {isImage
            ? <img src={url} alt={att.filename} className="max-h-[50vh] rounded border border-slate-200" />
            : <iframe src={url} title={att.filename} className="w-full h-[55vh] rounded border border-slate-200" />}
        </div>
      )}
    </div>
  );
}

